import { useState, useEffect } from 'react';

const CACHE_KEY = 'skip_geo_country';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// Fast client-side guess before the IP lookup resolves
function guessCountryFromBrowser() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname.endsWith('.ca')) return 'CA';
  const lang = (typeof navigator !== 'undefined' ? navigator.language : '') || '';
  if (/^en-CA|^fr-CA|^fr-QC/i.test(lang)) return 'CA';
  if (/^en-US/i.test(lang)) return 'US';
  return null;
}

export function useGeoCountry() {
  const [country, setCountry] = useState(guessCountryFromBrowser);

  useEffect(() => {
    // Check localStorage cache first
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.ts < CACHE_TTL) {
        setCountry(cached.code);
        return;
      }
    } catch {}

    // IP lookup for accuracy
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(data => {
        const code = data?.country_code || null;
        if (code) {
          setCountry(code);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ code, ts: Date.now() }));
          } catch {}
        }
      })
      .catch(() => {}); // keep browser guess on failure
  }, []);

  return country;
}

// SVG flag components — renders correctly on all platforms including Windows
const FLAGS = {
  CA: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 900 600"
      width="36"
      height="24"
      role="img"
      aria-label="Canada"
    >
      <rect width="900" height="600" fill="#D52B1E" />
      <rect x="225" width="450" height="600" fill="#fff" />
      <path fill="#D52B1E" d="M450 120l27 83h87l-70 51 27 83-71-51-71 51 27-83-70-51h87z" />
    </svg>
  ),
  US: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 190 100"
      width="36"
      height="19"
      role="img"
      aria-label="United States"
    >
      <rect width="190" height="100" fill="#B22234" />
      {[0, 1, 2, 3, 4, 5].map(i => (
        <rect key={i} y={i * 15.38 + 7.69} width="190" height="7.69" fill="#fff" />
      ))}
      <rect width="76" height="53.85" fill="#3C3B6E" />
    </svg>
  ),
  GB: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 60 30"
      width="36"
      height="18"
      role="img"
      aria-label="United Kingdom"
    >
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  ),
  AU: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 60 30"
      width="36"
      height="18"
      role="img"
      aria-label="Australia"
    >
      <rect width="60" height="30" fill="#00008B" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  ),
  GLOBAL: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 36 36"
      width="36"
      height="36"
      role="img"
      aria-label="Global"
    >
      <circle cx="18" cy="18" r="18" fill="#1A9E85" />
      <ellipse cx="18" cy="18" rx="8" ry="18" fill="none" stroke="#fff" strokeWidth="1.5" />
      <line x1="0" y1="18" x2="36" y2="18" stroke="#fff" strokeWidth="1.5" />
      <circle cx="18" cy="18" r="17" fill="none" stroke="#fff" strokeWidth="1.5" />
    </svg>
  ),
};

// Country metadata — add new countries here as we expand
export const COUNTRY_CONFIG = {
  CA: {
    flag: FLAGS.CA,
    flagEmoji: '🇨🇦',
    name: 'Canada',
    tagline: 'Skip the dealership. Keep the savings.',
    sub: "Canada's trusted private vehicle marketplace",
    currency: 'CAD',
  },
  US: {
    flag: FLAGS.US,
    flagEmoji: '🇺🇸',
    name: 'United States',
    tagline: 'Skip the dealership. Keep the savings.',
    sub: "America's trusted private vehicle marketplace",
    currency: 'USD',
  },
  GB: {
    flag: FLAGS.GB,
    flagEmoji: '🇬🇧',
    name: 'United Kingdom',
    tagline: 'Skip the dealership. Keep the savings.',
    sub: "The UK's trusted private vehicle marketplace",
    currency: 'GBP',
  },
  AU: {
    flag: FLAGS.AU,
    flagEmoji: '🇦🇺',
    name: 'Australia',
    tagline: 'Skip the dealership. Keep the savings.',
    sub: "Australia's trusted private vehicle marketplace",
    currency: 'AUD',
  },
};

export function getCountryConfig(code) {
  return (
    COUNTRY_CONFIG[code] || {
      flag: FLAGS.GLOBAL,
      flagEmoji: '🌐',
      name: 'Global',
      tagline: 'Skip the dealership. Keep the savings.',
      sub: 'The trusted private vehicle marketplace',
      currency: 'CAD',
    }
  );
}
